/**
 * @fileoverview A tela de entrada (30/10/2026, etapa 4 dos planos): e-mail e
 * senha, "manter conectado", o segundo passo do MFA (código ou código reserva)
 * e o cadastro do MFA obrigatório, no layout dividido das telas de entrada. O
 * "Crie sua conta" só aparece com o cadastro aberto no servidor; "Conheça os
 * planos" só com REACT_APP_PLANS_URL. Vindo da confirmação do e-mail fora do
 * navegador do cadastro, o e-mail já vem preenchido: falta só a senha.
 */

import React, { useState, useContext, useCallback, useEffect, useRef } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { openApi } from "../../services/api";
import AuthLayout from "../../components/AuthLayout";
import useLanguage from "../../hooks/useLanguage";
import { useAuthFormStyles } from "../../components/AuthLayout/formStyles";
import { EMAIL_RE } from "../../components/AuthLayout/authHelpers";
import MfaSetupDialog from "../../components/MfaSetupDialog";

const PLANS_URL = process.env.REACT_APP_PLANS_URL;

const Login = () => {
  useLanguage(); // trocou o idioma no globo: redesenha já traduzida
  const classes = useAuthFormStyles();
  const location = useLocation();
  const emailConfirmed = location.state?.emailConfirmed === true;
  const confirmedEmail = emailConfirmed ? location.state.email || "" : "";

  const [user, setUser] = useState({ email: confirmedEmail, password: "" });
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // O login bem-sucedido navega para o app e desmonta esta tela: depois do
  // await, nenhum setState (nem o do finally).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);

  const [step, setStep] = useState("credentials");
  const [mfaTicket, setMfaTicket] = useState("");
  const [setupTicket, setSetupTicket] = useState("");
  const [otp, setOtp] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState("");

  const { handleLogin, handleLoginMfa } = useContext(AuthContext);

  useEffect(() => {
    openApi
      .get("/auth/signup")
      .then(({ data }) => mountedRef.current && setSignupOpen(data?.enabled === true))
      .catch(() => undefined);
  }, []);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = useCallback(() => {
    const next = { email: "", password: "" };
    if (!user.email.trim()) next.email = i18n.t("login.errors.emailRequired");
    else if (!EMAIL_RE.test(user.email.trim())) next.email = i18n.t("login.errors.emailInvalid");
    if (!user.password) next.password = i18n.t("login.errors.passwordRequired");
    setErrors(next);
    return !next.email && !next.password;
  }, [user.email, user.password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || !validate()) return;
    setLoading(true);
    try {
      const result = await handleLogin({ email: user.email.trim(), password: user.password, remember });
      if (!mountedRef.current) return;
      if (result?.mfaRequired) {
        setMfaTicket(result.mfaTicket);
        setStep("otp");
      } else if (result?.mfaSetupRequired) {
        setSetupTicket(result.setupTicket);
        setStep("setup");
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleSubmitOtp = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await handleLoginMfa({ mfaTicket, otp: useBackup ? undefined : otp, backupCode: useBackup ? backupCode : undefined });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep("credentials");
    setMfaTicket("");
    setOtp("");
    setBackupCode("");
    setUseBackup(false);
  };

  const handleCapsLock = (e) => {
    if (typeof e.getModifierState === "function") setCapsLockOn(e.getModifierState("CapsLock"));
  };

  return (
    <AuthLayout>
      {step === "otp" ? (
        <form className={classes.view} noValidate onSubmit={handleSubmitOtp}>
          <h1 className={classes.title}>{i18n.t("mfa.login.title")}</h1>
          <p className={classes.subtitle}>
            {useBackup
              ? i18n.t("mfa.login.backupHint") || i18n.t("mfa.login.backupLabel")
              : i18n.t("mfa.login.otpHint") || i18n.t("mfa.login.otpLabel")}
          </p>
          <div className={classes.field}>
            <label className={classes.label} htmlFor="login-otp">
              {useBackup ? i18n.t("mfa.login.backupLabel") : i18n.t("mfa.login.otpLabel")}
            </label>
            {useBackup ? (
              <input
                id="login-otp"
                className={classes.input}
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.trim())}
                autoFocus
                autoComplete="one-time-code"
                disabled={loading}
              />
            ) : (
              <input
                id="login-otp"
                className={classes.input}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="••••••"
                style={{ textAlign: "center", fontSize: 22, letterSpacing: 8 }}
                disabled={loading}
              />
            )}
          </div>
          <button type="submit" className={classes.button} disabled={loading || (useBackup ? backupCode.length < 8 : otp.length < 6)}>
            {loading && <CircularProgress size={18} className={classes.spinner} />}
            {i18n.t("mfa.login.verify")}
          </button>
          <div className={classes.alt} style={{ justifyContent: "space-between" }}>
            <button type="button" className={classes.link} onClick={() => setUseBackup((value) => !value)}>
              {useBackup ? i18n.t("mfa.login.useOtp") : i18n.t("mfa.login.useBackup")}
            </button>
            <button type="button" className={classes.link} onClick={handleBackToCredentials} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <ArrowLeft size={15} />
              {i18n.t("mfa.login.backToPassword")}
            </button>
          </div>
        </form>
      ) : (
        <form className={classes.view} noValidate onSubmit={handleSubmit}>
          <h1 className={classes.title}>{i18n.t("auth.login.title")}</h1>
          <p className={classes.subtitle}>{i18n.t("auth.login.subtitle")}</p>
          {emailConfirmed && <div className={`${classes.notice} ${classes.noticeOk}`} role="status">{i18n.t("auth.verify.ok")}</div>}

          <div className={classes.field}>
            <label className={classes.label} htmlFor="login-email">{i18n.t("auth.fields.email")}</label>
            <input
              id="login-email"
              name="email"
              type="email"
              className={classes.input}
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus={!confirmedEmail}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? "login-email-error" : undefined}
            />
            {errors.email && <div id="login-email-error" className={classes.error}>{errors.email}</div>}
          </div>

          <div className={classes.field}>
            <label className={classes.label} htmlFor="login-password">{i18n.t("auth.fields.password")}</label>
            <div className={classes.inputWrap}>
              <input
                id="login-password"
                name="password"
                type={showPassword ? "text" : "password"}
                className={`${classes.input} ${classes.inputWithButton}`}
                value={user.password}
                onChange={handleChangeInput}
                onKeyUp={handleCapsLock}
                onKeyDown={handleCapsLock}
                autoComplete="current-password"
                autoFocus={Boolean(confirmedEmail)}
                aria-invalid={Boolean(errors.password)}
                aria-describedby={errors.password ? "login-password-error" : undefined}
              />
              <button
                type="button"
                className={classes.eye}
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? i18n.t("auth.hidePassword") : i18n.t("auth.showPassword")}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && <div id="login-password-error" className={classes.error}>{errors.password}</div>}
            {capsLockOn && <div className={classes.hint} role="status">{i18n.t("login.capsLockOn")}</div>}
          </div>

          <div className={classes.row}>
            <label className={classes.check}>
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              {i18n.t("auth.login.remember")}
            </label>
            <RouterLink to="/forgot-password" className={classes.link}>{i18n.t("auth.login.forgot")}</RouterLink>
          </div>

          <button type="submit" className={classes.button} disabled={loading}>
            {loading && <CircularProgress size={18} className={classes.spinner} />}
            {loading ? i18n.t("login.buttons.submitting") : i18n.t("login.buttons.submit")}
          </button>

          {(signupOpen || PLANS_URL) && (
            <div className={classes.alt}>
              {signupOpen && (
                <>
                  <span>{i18n.t("auth.login.noAccount")}</span>
                  <RouterLink to="/signup" className={classes.link}>{i18n.t("auth.login.createAccount")}</RouterLink>
                </>
              )}
              {PLANS_URL && (
                <a href={PLANS_URL} className={classes.link} target="_blank" rel="noopener noreferrer" style={{ marginLeft: signupOpen ? "auto" : 0 }}>
                  {i18n.t("auth.login.plans")}
                </a>
              )}
            </div>
          )}
        </form>
      )}

      <MfaSetupDialog
        open={step === "setup"}
        setupTicket={setupTicket}
        forceMode
        onClose={() => {
          setStep("credentials");
          setSetupTicket("");
        }}
      />
    </AuthLayout>
  );
};

export default Login;
